import uuid from '@vend/vend-uuid'
import {
  ApiLineItemTaxComponent,
  ApiProduct,
  ApiTax,
  ApiTaxRate,
  User,
} from '@vend/types'
import { calculateCustomTaxRates } from '../sale-calculation/calculate-line-item'
import { PromotionLineItem } from '../components/sale/sale-promotion/sale-promotion-resource'
import { Promotion, PromotionDetails } from './promotion'
import { CustomFieldValue } from './custom-fields'
import { FulfilmentType } from './fulfilment'

export interface LineItemInput {
  id?: string
  localId?: string
  product: ApiProduct
  dateAdded?: number
  displayPrice: number | string
  displayDiscount: number | string
  displayDiscountPercent: number | string
  displayMarkupPercent: number | string
  displayBasePrice: number | string
  displayBestDiscountPercent: number | string
  displayBestDiscountPrice: number | string
  taxComponents?: LineItemTaxComponent[]
  giftCardNumber?: string
  giftCardReload?: boolean
  giftCardClientId?: string
  giftCardExpiresAt?: string
  price?: string
  basePrice?: string
  tax?: string
  baseTax?: string
  taxInfo?: ApiTax
  promotionDetails?: PromotionDetails[] | null
  lineItemPromotion?: PromotionLineItem | null
  loyaltyValue?: string
  explicitLoyaltyValue?: boolean
  note?: string
  quantity?: number | string
  displayQuantity?: number | string
  confirmed?: boolean
  adjusted?: boolean
  customerTaxApplied?: boolean
  isReturn?: boolean
  totals?: LineItemTotals
  registerId: string
  registerSequenceId: string
  salesperson?: LineItemUser
  customFields?: CustomFieldValue[]
  fulfilmentType?: FulfilmentType
  fulfilmentCompleted?: boolean
}

export interface LineItemTotals {
  baseTax?: number | string
  displayBasePrice?: number | string
  displayPrice?: number | string
  exclusivePrice: number | string
  exclusiveBasePrice: number | string
  tax: number | string
  inclusivePrice: number | string
  inclusiveBasePrice: number | string
  // total exclusive price deduction from sale discount
  saleDiscount: number | string
  // total tax price deduction from sale discount
  saleDiscountTax: number | string
}

export interface LineItemTaxComponent extends ApiLineItemTaxComponent {
  base_total_tax: number
  rate: ApiTaxRate
  total_tax_after_sale_discount: number
}

export interface LineItem {
  /**
   * The unique ID of this LineItem locally generated on the client.
   */
  localId: string
  /**
   * The unique ID of this LineItem generated on the server. Note: when the sale has not been posted to the server, it will not have an ID.
   */
  id?: string
  /**
   * Used to determine applicable promotions to un-adjusted line items and as a flag for custom price.
   */
  adjusted?: boolean
  /**
   * Used to determine whether should refresh tax for adjusted lineItems and as a flag for customer tax applied.
   */
  customerTaxApplied?: boolean
  /**
   * If this line item is part of returned sale
   */
  isReturn: boolean
  product: ApiProduct
  giftCardNumber?: string
  giftCardReload: boolean
  giftCardClientId?: string
  /**
   * Set the expiry date of a gift card item. Only set if the retailer has set expiry options when setting up gift cards.
   */
  giftCardExpiresAt?: string
  /**
   * Current adjusted price (difference between basePrice and price is discount)
   */
  price: number | string
  /**
   * Base price before any promotions or adjustments
   */
  basePrice: string
  /**
   * Value of tax rate against price
   */
  tax: number | string
  /**
   * Value of tax rate against base price for use when totaling inclusive base value
   */
  baseTax: string
  taxInfo?: ApiTax
  lineItemPromotion?: PromotionLineItem | null
  promotionDetails?: PromotionDetails[] | null
  loyaltyValue: number | string
  /**
   * Whether the loyaltyValue was explicitly set via price book or calculated via the loyalty ratio
   */
  explicitLoyaltyValue: boolean
  /**
   * The line item note.
   */
  note: string
  /**
   * The date / time the lineItem was added to the sale. This is used to correctly sort line items displayed in the sale list.
   */
  dateAdded: number
  /**
   * The line item price for display (price per unit, so not affected by quantity, & either tax inclusive or
   * tax exclusive depending on register tax settings)
   */
  displayPrice: number | string
  /**
   * This is the discount value that gets posted in the end.
   */
  displayDiscount: number | string
  /**
   * The discount percent for display.
   */
  displayDiscountPercent: number | string
  /**
   * The markup percent for display (i.e. when discount value is negative).
   */
  displayMarkupPercent: number | string
  /**
   * The best pricebook discount percent for display.
   */
  displayBestDiscountPercent: number | string
  /**
   * The best pricebook price for display.
   */
  displayBestDiscountPrice: number | string
  /**
   * The original base price, excluding discount.
   */
  displayBasePrice: number | string
  /**
   * The line item quantity.
   */
  quantity: number | string
  /**
   * The original line item quantity.
   *
   * If a line item is a return, you shouldn't be able to change the quantity to a value lower than the original, so
   * we need to set the original quantity to validate against.
   */
  originalQuantity: number | string
  /**
   * The quantity for display (no extra calcs compared to quantity, but allows valid quantity to still display
   * when this value is input as invalid).
   *
   * TODO: is this ever different from quantity? Do we need it?
   */
  displayQuantity: number | string
  /**
   * Line item tax components are the total tax amounts grouped by rate id.
   *
   * This data comes from the 2.0 sales API and is the most correct version of tax data.
   * Currently used for the tax summary in sales history.
   */
  taxComponents: LineItemTaxComponent[]
  /**
   * The totals for this line item.
   */
  totals: LineItemTotals
  /**
   * Identify that the line has been confirmed
   */
  confirmed: boolean
  /**
   * Id of the register used to add this item to the sale
   */
  registerId: string
  /**
   * Register sequence id used to add this item to the sale
   */
  registerSequenceId: string
  /**
   * Indicates that the line item prices etc are still being calculated.
   */
  calculating: boolean
  /**
   * User responsible for this sale item
   */
  salesperson: LineItemUser | null
  /**
   * The type of fulfilment for this line item
   */
  fulfilmentType?: FulfilmentType
  /**
   * Indicates that the fulfilment for this line item has been marked as completed
   */
  fulfilmentCompleted?: boolean
  /**
   * Custom fields for the line item
   */
  customFields: CustomFieldValue[]
}

/**
 * Represents a line item in a sale, directly linked to display.
 */
export function createLineItem(data: LineItemInput): LineItem {
  data = data || {}

  const totals: LineItemTotals = {
    exclusivePrice: '0',
    exclusiveBasePrice: '0',
    tax: '0',
    inclusivePrice: '0',
    inclusiveBasePrice: '0',
    // total exclusive price deduction from sale discount
    saleDiscount: '0',
    // total tax price deduction from sale discount
    saleDiscountTax: '0',
    ...(data.totals || {}),
  }

  const lineItem: LineItem = {
    localId: data.localId || uuid(),
    id: data.id,

    /**
     * Props migrated from SaleItem
     * -------------------------------------------------
     */
    adjusted: data.adjusted || false,
    customerTaxApplied: data.customerTaxApplied || false,
    isReturn: data.isReturn || false,
    product: data.product,
    giftCardNumber: data.giftCardNumber || '',
    giftCardReload: data.giftCardReload || false,
    giftCardClientId: data.giftCardClientId,
    giftCardExpiresAt: data.giftCardExpiresAt,
    price: data.price || '0',
    basePrice: data.basePrice || '0',
    tax: data.tax || '0',
    baseTax: data.baseTax || '0',
    taxInfo: getTaxInfo({
      taxInfo: data.taxInfo,
      totals,
      taxComponents: data.taxComponents || [],
      isReturn: data.isReturn || false,
      confirmed: data.confirmed || false,
    }),
    lineItemPromotion: data.lineItemPromotion,
    promotionDetails: data.promotionDetails,
    loyaltyValue: data.loyaltyValue || '0',
    explicitLoyaltyValue: data.explicitLoyaltyValue || false,
    /* ------------------------------------------------- */

    note: data.note || '',
    calculating: true,
    dateAdded: data.dateAdded || Date.now(),
    displayPrice: data.displayPrice || '0',
    displayDiscount: data.displayDiscount || '0',
    displayDiscountPercent: data.displayDiscountPercent || '0',
    displayMarkupPercent: data.displayMarkupPercent || '0',
    displayBestDiscountPercent: data.displayBestDiscountPercent || '0',
    displayBestDiscountPrice: data.displayBestDiscountPrice || '0',
    displayBasePrice: data.displayBasePrice || '0',
    quantity: data.quantity === undefined ? '1' : data.quantity,
    originalQuantity: data.quantity === undefined ? '1' : data.quantity,
    displayQuantity:
      data.displayQuantity ||
      (data.quantity === undefined ? '1' : data.quantity),
    taxComponents: data.taxComponents || [],
    totals,
    confirmed: data.confirmed || false,
    registerId: data.registerId || '',
    registerSequenceId: data.registerSequenceId || '',
    salesperson: data.salesperson || null,
    fulfilmentType: data.fulfilmentType || FulfilmentType.UNSPECIFIED,
    fulfilmentCompleted: data.fulfilmentCompleted || false,
    customFields: data.customFields || [],
  }

  return lineItem
}

type TaxInfoParams = Pick<
  LineItem,
  'taxInfo' | 'totals' | 'taxComponents' | 'isReturn' | 'confirmed'
>
function getTaxInfo({
  taxInfo,
  totals,
  taxComponents,
  isReturn = false,
  confirmed = false,
}: TaxInfoParams): ApiTax | undefined {
  let calculatedTaxInfo = taxInfo

  // Detect if there are any custom tax rates (where a different rate is used to the rate associated with the
  // tax ID) used on this line item (only applies to return or confirmed line items). If so, we need to respect
  // these custom tax rates in all our calculations and so update the taxInfo with the custom rates.
  // See https://vendhq.atlassian.net/browse/LR-8488 for more details.
  if (
    calculatedTaxInfo?.rates?.length &&
    totals?.exclusivePrice !== undefined
  ) {
    const customRates = calculateCustomTaxRates({
      totalExclusivePrice: totals.exclusivePrice,
      rates: calculatedTaxInfo.rates,
      taxComponents,
      isReturn,
      isConfirmed: confirmed,
    })

    if (customRates?.length) {
      calculatedTaxInfo = {
        ...calculatedTaxInfo,
        rates: customRates,
      }
    }
  }

  return calculatedTaxInfo
}

/**
 * Represents an unknown line item added to a sale (for instance a scanned product that does not exist in the store).
 */
export interface UnknownLineItem {
  localId: string
  name: string
  /**
   * The date / time the lineItem was added to the sale. This is used to correctly sort line items displayed in the sale list.
   */
  dateAdded: number
}

/**
 * Type of line item stored in IndexedDB. Has no methods, getters, etc.
 */
export type LineItemJson = LineItem & {
  // promotion is deprecated in LineItem, but needs to be supported here
  // for backwards compatability with old sales data stored in indexedDB
  promotion?: Promotion | null
}

export type LineItemUser = Pick<
  User,
  'id' | 'displayName' | 'username' | 'email'
>

export const LINE_ITEM_NOTE_LENGTH = 2000

export const isLineItemNoteTooLong = (note?: string) => {
  return (note?.length ?? 0) > LINE_ITEM_NOTE_LENGTH
}
